/**
 * CFO & Advisory › Financial Statements: QuickBooks' P&L, balance sheet and cash flows for a
 * period, with budget, forecast, prior-period and prior-year columns. Every amount is the
 * server's (QuickBooks' figures summed in cents); clicking an account's amount opens the
 * transactions behind it.
 */
import { formatCents, NOT_MEANINGFUL_HELP, type Variance } from "@clientflow/shared";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Download, Printer } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Empty, Loading } from "@/components/page";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errorMessage } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { TableScroll } from "@/components/table-scroll";

type Statement = "pnl" | "bs" | "cf";
type View = "month" | "quarter" | "ytd" | "ttm";
type Row = {
  label: string;
  accountId: string | null;
  depth: number;
  kind: "data" | "header" | "total";
  group: string | null;
  higherIsBetter: boolean | null;
  actual: number | null;
  budget: number | null;
  /** From the formula library: dollars always; the percentage may be "n/m". */
  variance: Pick<Variance, "dollars" | "pct" | "favorable"> | null;
  forecast: number | null;
  previous: number | null;
  priorYear: number | null;
};
type Data = {
  statement: Statement;
  title: string;
  view: View;
  end: string;
  basis: "Accrual" | "Cash";
  period: string;
  asOf: string;
  open: string[];
  months: string[];
  rows: Row[];
  budgetNames?: string[];
  forecast?: { version: number; createdAt: string } | null;
  compareLabels: { previous: string | null; priorYear: string };
};
type Ledger = { name: string | null; beginning: number | null; total: number; rowCount: number; truncated: boolean; rows: Array<{ date: string; type: string; num: string; name: string; memo: string; split: string; amount: number }> };

const STATEMENTS: Array<[Statement, string]> = [
  ["pnl", "Profit & Loss"],
  ["bs", "Balance Sheet"],
  ["cf", "Cash Flows"],
];
const VIEWS: Array<[View, string, string]> = [
  ["month", "Month", "One month"],
  ["quarter", "Quarter", "Quarter of the fiscal year"],
  ["ytd", "YTD", "Year to date"],
  ["ttm", "TTM", "Trailing twelve months"],
];

/** Accounting style: negatives in parentheses, no dollar sign on detail lines. */
function amt(c: number | null, opts: { sign?: boolean } = {}): string {
  if (c === null) return "";
  const s = formatCents(Math.abs(c));
  const body = opts.sign ? s : s.slice(1);
  return c < 0 ? `(${body})` : body;
}

/** The statement's bottom line, double-underlined as in printed statements. */
const FINAL = new Set(["NetIncome", "TotalAssets", "TotalLiabilitiesAndEquity", "EndingCash"]);

const monthName = (p: string) => new Date(`${p}-15T12:00:00`).toLocaleDateString("en-US", { month: "short", year: "numeric" });

function lastMonths(n: number): string[] {
  const d = new Date();
  return Array.from({ length: n }, (_, i) => {
    const x = new Date(d.getFullYear(), d.getMonth() - i, 1);
    return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}`;
  });
}

function pctChange(cur: number | null, base: number | null): string {
  if (cur === null || base === null || base === 0) return "";
  const v = ((cur - base) / Math.abs(base)) * 100;
  return `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(v).toFixed(1)}%`;
}

function Seg<T extends string>({ value, options, onChange, label }: { value: T; options: Array<[T, string, string?]>; onChange: (v: T) => void; label: string }) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex rounded-md border border-input bg-background p-0.5">
      {options.map(([k, l, title]) => (
        <button key={k} type="button" role="radio" aria-checked={value === k} title={title} onClick={() => onChange(k)} className={cn("rounded px-2.5 py-1 text-xs font-medium", value === k ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground")}>
          {l}
        </button>
      ))}
    </div>
  );
}

type Column = { key: string; label: string; value: (r: Row) => string; tone?: (r: Row) => "good" | "bad" | null; drill?: boolean; title?: (r: Row) => string | undefined };

function columnsFor(d: Data): Column[] {
  const prevLabel = d.compareLabels.previous;
  if (d.statement === "bs") {
    return [
      { key: "actual", label: monthName(d.end), value: (r) => amt(r.actual), drill: true },
      ...(prevLabel ? [{ key: "previous", label: prevLabel, value: (r: Row) => amt(r.previous) }, { key: "chg1", label: "Change", value: (r: Row) => (r.actual !== null && r.previous !== null ? amt(r.actual - r.previous) : "") }] : []),
      { key: "priorYear", label: d.compareLabels.priorYear, value: (r) => amt(r.priorYear) },
      { key: "chg2", label: "Change", value: (r) => (r.actual !== null && r.priorYear !== null ? amt(r.actual - r.priorYear) : "") },
    ];
  }
  const cols: Column[] = [{ key: "actual", label: "Actual", value: (r) => amt(r.actual), drill: true }];
  if (d.statement === "pnl" && d.budgetNames?.length) {
    cols.push(
      { key: "budget", label: "Budget", value: (r) => amt(r.budget) },
      { key: "var", label: "Variance", value: (r) => (r.variance ? amt(r.variance.dollars) : ""), tone: (r) => (r.variance?.favorable === true ? "good" : r.variance?.favorable === false ? "bad" : null) },
      { key: "varp", label: "Var %", value: (r) => r.variance?.pct.display ?? "", tone: (r) => (r.variance?.favorable === true ? "good" : r.variance?.favorable === false ? "bad" : null), title: (r) => (r.variance?.pct.kind === "not_meaningful" ? `${r.variance.pct.reason}. ${NOT_MEANINGFUL_HELP}` : undefined) },
    );
  }
  if (d.statement === "pnl" && d.forecast) cols.push({ key: "forecast", label: "Forecast", value: (r) => amt(r.forecast) });
  if (prevLabel) cols.push({ key: "previous", label: prevLabel, value: (r) => amt(r.previous) });
  cols.push(
    { key: "priorYear", label: d.compareLabels.priorYear, value: (r) => amt(r.priorYear) },
    {
      key: "pyc",
      label: "Change",
      // Cash flows can change sign, so their change is in dollars; P&L lines as a percentage.
      value: (r) => (d.statement === "cf" ? (r.actual !== null && r.priorYear !== null ? amt(r.actual - r.priorYear) : "") : pctChange(r.actual, r.priorYear)),
      tone: (r) => {
        if (r.higherIsBetter === null || r.actual === null || r.priorYear === null || r.actual === r.priorYear) return null;
        return (r.actual > r.priorYear) === r.higherIsBetter ? "good" : "bad";
      },
    },
  );
  return cols;
}

function toCsv(d: Data, cols: Column[]): string {
  const esc = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const lines = [[d.title, d.period, `${d.basis} basis`].map(esc).join(","), ["Account", ...cols.map((c) => c.label)].map(esc).join(",")];
  for (const r of d.rows) lines.push([`${"  ".repeat(r.depth)}${r.label}`, ...cols.map((c) => c.value(r))].map(esc).join(","));
  return lines.join("\n");
}

function LedgerDialog({ clientId, scope, row, months, basis, statement, onClose }: { clientId: string; scope: "cfo" | "books"; row: Row; months: string[]; basis: string; statement: Statement; onClose: () => void }) {
  const from = months[0]!;
  const to = months[months.length - 1]!;
  const { data, isLoading, error } = useQuery<Ledger>({ queryKey: [`/api/clients/${clientId}/${scope}/statements/ledger?${new URLSearchParams({ account: row.accountId!, from, to, basis })}`] });
  // P&L lines are the period's activity; balance-sheet lines are the closing balance (opening + activity).
  const expected = row.actual;
  const shown = data ? (statement === "bs" ? (data.beginning ?? 0) + data.total : data.total) : null;
  const ties = statement !== "cf" && shown !== null && expected !== null ? shown === expected : null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>{row.label}</DialogTitle>
          <DialogDescription>
            Transactions from QuickBooks' General Ledger, {from === to ? monthName(from) : `${monthName(from)} – ${monthName(to)}`}, {basis.toLowerCase()} basis.
          </DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <Loading />
        ) : error || !data ? (
          <Empty>{errorMessage(error)}</Empty>
        ) : (
          <TableScroll label={`${row.label} transactions table`} both className="max-h-[60vh]">
            <table className="w-full min-w-[680px] text-sm">
              <thead className="sticky top-0 bg-card text-left text-xs text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="py-2 pr-2 font-medium">Date</th>
                  <th className="px-2 py-2 font-medium">Type</th>
                  <th className="px-2 py-2 font-medium">Num</th>
                  <th className="px-2 py-2 font-medium">Name</th>
                  <th className="px-2 py-2 font-medium">Memo</th>
                  <th className="px-2 py-2 font-medium">Split</th>
                  <th className="py-2 pl-2 text-right font-medium">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.beginning !== null && (
                  <tr className="text-muted-foreground">
                    <td className="py-1.5 pr-2" colSpan={6}>
                      Opening balance
                    </td>
                    <td className="tabular py-1.5 pl-2 text-right">{amt(data.beginning, { sign: true })}</td>
                  </tr>
                )}
                {data.rows.map((x, i) => (
                  <tr key={i}>
                    <td className="whitespace-nowrap py-1.5 pr-2">{formatDate(x.date)}</td>
                    <td className="px-2 py-1.5">{x.type}</td>
                    <td className="px-2 py-1.5 text-muted-foreground">{x.num}</td>
                    <td className="px-2 py-1.5">{x.name}</td>
                    <td className="px-2 py-1.5 text-muted-foreground">{x.memo}</td>
                    <td className="px-2 py-1.5 text-muted-foreground">{x.split}</td>
                    <td className="tabular py-1.5 pl-2 text-right">{amt(x.amount, { sign: true })}</td>
                  </tr>
                ))}
                {!data.rows.length && (
                  <tr>
                    <td colSpan={7} className="py-6 text-center text-muted-foreground">
                      No transactions in this period.
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-border font-semibold">
                  <td colSpan={6} className="py-2 pr-2">
                    {statement === "bs" ? "Closing balance" : "Total"}
                  </td>
                  <td className="tabular py-2 pl-2 text-right">{amt(shown, { sign: true })}</td>
                </tr>
              </tfoot>
            </table>
            {data.truncated && (
              <p className="mt-2 text-xs text-muted-foreground">
                Showing the first {data.rows.length.toLocaleString()} of {data.rowCount.toLocaleString()} transactions. The total and closing balance include all of them; open QuickBooks for the full list.
              </p>
            )}
            {ties !== null && (
              <p className={cn("mt-2 text-xs", ties ? "text-emerald-700 dark:text-emerald-400" : "text-destructive")}>
                {ties ? "Agrees with the statement." : `Does not agree with the statement (${amt(expected, { sign: true })}). Refresh from QuickBooks; the books may have changed.`}
              </p>
            )}
          </TableScroll>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function FinancialStatements({ clientId, scope = "cfo" }: { clientId: string; scope?: "cfo" | "books" }) {
  const months = useMemo(() => lastMonths(25), []);
  const [statement, setStatement] = useState<Statement>("pnl");
  const [view, setView] = useState<View>("month");
  const [end, setEnd] = useState(months[1]!);
  const [basis, setBasis] = useState<"Accrual" | "Cash">("Accrual");
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const [drill, setDrill] = useState<Row | null>(null);
  const qs = new URLSearchParams({ statement, view, end, basis });
  // Collapsed headings are remembered by row position, which means nothing on another statement or period.
  useEffect(() => setCollapsed(new Set()), [statement, view, end, basis]);
  const { data, isLoading, error, isFetching } = useQuery<Data>({ queryKey: [`/api/clients/${clientId}/${scope}/statements?${qs}`], retry: false, placeholderData: (p) => p });
  const cols = data ? columnsFor(data) : [];

  // Rows hidden under a collapsed heading (until that section's total line).
  const visible = useMemo(() => {
    if (!data) return [];
    const out: Array<{ r: Row; i: number }> = [];
    let hideBelow: number | null = null;
    data.rows.forEach((r, i) => {
      if (hideBelow !== null) {
        if (r.kind === "total" && r.depth === hideBelow) hideBelow = null;
        else return;
      }
      out.push({ r, i });
      if (r.kind === "header" && collapsed.has(i)) hideBelow = r.depth;
    });
    return out;
  }, [data, collapsed]);

  const download = () => {
    if (!data) return;
    const blob = new Blob([toCsv(data, cols)], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${data.title} ${data.period}.csv`.replace(/[^\w .–-]/g, "");
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 border-b border-border pb-3 print:hidden">
        <Seg label="Statement" value={statement} options={STATEMENTS} onChange={setStatement} />
        <Seg label="Period" value={view} options={VIEWS} onChange={setView} />
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          {statement === "bs" ? "As of" : "Ending"}
          <select aria-label="Period end" className="h-8 rounded-md border border-input bg-background px-2 text-xs font-medium" value={end} onChange={(e) => setEnd(e.target.value)}>
            {months.map((m) => (
              <option key={m} value={m}>
                {monthName(m)}
                {m === months[0] ? " (in progress)" : ""}
              </option>
            ))}
          </select>
        </label>
        <Seg label="Basis" value={basis} options={[["Accrual", "Accrual"], ["Cash", "Cash"]]} onChange={setBasis} />
        <div className="ml-auto flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={download} disabled={!data}>
            <Download className="h-3.5 w-3.5" /> CSV
          </Button>
          <Button size="sm" variant="ghost" onClick={() => window.print()} disabled={!data}>
            <Printer className="h-3.5 w-3.5" /> Print
          </Button>
        </div>
      </div>

      {isLoading ? (
        <Loading />
      ) : error || !data ? (
        <Empty>{errorMessage(error)}</Empty>
      ) : (
        <section className={cn("rounded-lg border border-card-border bg-card", isFetching && "opacity-80")}>
          <header className="border-b border-border px-5 py-4 text-center">
            <h3 className="text-lg font-semibold">{data.title}</h3>
            <p className="text-sm text-muted-foreground">
              {data.statement === "bs" ? `As of ${monthName(data.end)}` : data.period} · {data.basis} basis
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              From QuickBooks as of {formatDate(data.asOf)}
              {data.budgetNames?.length ? ` · Budget: ${data.budgetNames.join(", ")}` : ""}
              {data.forecast ? ` · Forecast: version ${data.forecast.version}` : ""}
            </p>
            {data.open.length > 0 && <p className="mt-2 inline-block rounded bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">Preliminary: books not closed for {data.open.map(monthName).join(", ")}</p>}
          </header>
          <TableScroll label={`${data.title} table`}>
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/60 text-xs text-muted-foreground">
                  <th className="px-4 py-2 text-left font-medium">Account</th>
                  {cols.map((c) => (
                    <th key={c.key} className="whitespace-nowrap px-3 py-2 text-right font-medium">
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map(({ r, i }) => {
                  const isGrand = r.kind === "total" && r.depth === 0;
                  return (
                    <tr key={i} className={cn(r.kind === "total" && "font-semibold", isGrand && "border-t border-border bg-secondary/40", r.kind === "data" && "hover:bg-muted/40")}>
                      <td className="py-1.5 pr-3" style={{ paddingLeft: `${16 + r.depth * 18}px` }}>
                        {r.kind === "header" ? (
                          <button
                            type="button"
                            className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-foreground"
                            onClick={() => setCollapsed((s) => (s.has(i) ? new Set([...s].filter((x) => x !== i)) : new Set([...s, i])))}
                            aria-expanded={!collapsed.has(i)}
                          >
                            {collapsed.has(i) ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                            {r.label}
                          </button>
                        ) : (
                          r.label
                        )}
                      </td>
                      {cols.map((c) => {
                        const v = c.value(r);
                        const tone = c.tone?.(r);
                        const canDrill = c.drill && r.kind === "data" && r.accountId && r.actual !== null && data.statement !== "cf";
                        return (
                          <td key={c.key} title={c.title?.(r)} className={cn("tabular whitespace-nowrap px-3 py-1.5 text-right", tone === "good" && "text-emerald-700 dark:text-emerald-400", tone === "bad" && "text-red-700 dark:text-red-400", FINAL.has(r.group ?? "") && c.key === "actual" && "underline decoration-double underline-offset-4")}>
                            {canDrill ? (
                              <button type="button" className="tabular text-primary hover:underline" onClick={() => setDrill(r)} title="See the transactions">
                                {v}
                              </button>
                            ) : (
                              v
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableScroll>
          <p className="border-t border-border px-5 py-2.5 text-xs text-muted-foreground print:hidden">{data.statement === "cf" ? "Cash flows are QuickBooks' own statement; open the Balance Sheet to see the transactions behind each balance." : "Select an account's amount to see its transactions."} Negative amounts are shown in parentheses.{data.statement === "pnl" && data.budgetNames?.length ? ` ${NOT_MEANINGFUL_HELP}` : ""}</p>
        </section>
      )}
      {drill && data && <LedgerDialog clientId={clientId} scope={scope} row={drill} months={data.months} basis={data.basis} statement={data.statement} onClose={() => setDrill(null)} />}
    </div>
  );
}
